/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import { Checkbox, FormControlLabel, FormGroup } from "@material-ui/core";
import React, { useState } from "react";
import {
  Button, Col, Container, DropdownItem, DropdownMenu, DropdownToggle, Row, UncontrolledDropdown
} from "reactstrap";
import Toastr from 'toastr';
import { useStartAnalysisByFileMutation } from '../hooks/useStartAnalysisByFileMutation';
import { ConfType, ExecutionSteps } from "../types";
import { confTypeToString } from "../utils/confTypeToString";

const Scheduler: React.FC = () => {
  const [file, setFile] = useState<FileList>()
  const [confName, setConfName] = useState('')
  const [sessionName, setSessionName] = useState('')
  const [type, setType] = useState<ConfType>();
  const [selectedSteps, setSelectedSteps] = useState<ExecutionSteps[]>([
    ExecutionSteps.screenshot,
    ExecutionSteps.transcribe,
    ExecutionSteps.speakerTagging,
  ])

  const [{ mutate: startAnalysisByFile, isLoading }, progress] = useStartAnalysisByFileMutation((err, msg) => {
    if (err) {
      Toastr.error(msg || 'Something went wrong. Please try again later')
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } else {
      Toastr.success('Your request has been queued. You will receive an email notification upon completion.')
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    }
  })

  const handleAnalyse = () => {
    if (isLoading) {
      return
    }

    if (!confName) {
      alert('Conference name is required!')
      return
    }
    if (!sessionName) {
      alert('Session name is required!')
      return
    }
    if (!file || file.length === 0) {
      alert('Please select a file to upload!')
      return
    }
    if (!type) {
      alert('Please select conference type!')
      return
    }
    if (selectedSteps.includes(ExecutionSteps.speakerTagging) && !selectedSteps.includes(ExecutionSteps.transcribe)) {
      alert('Speaker tagging requires transcribe step to be selected!')
      return
    }
    if (!window.confirm('Are you sure you want to begin analysis of this file?')) {
      return
    }
    startAnalysisByFile({
      type,
      confName,
      sessionName,
      file: file.item(0)!,
      steps: selectedSteps
    })
  }

  return (
    <>
      <div className="header bg-gradient-info pb-6 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
            <Row>
              <Col className="my-2" md="6" xs="12">
                <div className="input-group-alternative input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">
                      <i className="fas fa-search"></i>
                    </span>
                  </div>
                  <input onChange={e => {
                    setConfName(e.target.value)
                  }} value={confName} placeholder="Conference name" type="text" className="form-control" />
                </div>
              </Col>
              <Col className="my-2" md="6" xs="12">
                <div className="input-group-alternative input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">
                      <i className="fas fa-search"></i>
                    </span>
                  </div>
                  <input onChange={e => {
                    setSessionName(e.target.value)
                  }} value={sessionName} placeholder="Session name" type="text" className="form-control" />
                </div>
              </Col>
            </Row>
            <Row className="mx-1">
              <FormGroup>
                <FormControlLabel control={<Checkbox checked={selectedSteps.includes(ExecutionSteps.screenshot)} value={ExecutionSteps.screenshot} onChange={e => {
                  if (e.target.checked && !selectedSteps.includes(ExecutionSteps.screenshot)) {
                    setSelectedSteps([...selectedSteps, ExecutionSteps.screenshot])
                    selectedSteps.push(ExecutionSteps.screenshot)
                  }
                  if (!e.target.checked && selectedSteps.includes(ExecutionSteps.screenshot)) {
                    setSelectedSteps(selectedSteps.filter(s => s !== ExecutionSteps.screenshot))
                  }
                }} />} label="Screenshot"></FormControlLabel>
              </FormGroup>
              <FormGroup>
                <FormControlLabel control={<Checkbox checked={selectedSteps.includes(ExecutionSteps.transcribe)} value={ExecutionSteps.transcribe} onChange={e => {
                  if (e.target.checked && !selectedSteps.includes(ExecutionSteps.transcribe)) {
                    setSelectedSteps([...selectedSteps, ExecutionSteps.transcribe])
                    selectedSteps.push(ExecutionSteps.transcribe)
                  }
                  if (!e.target.checked && selectedSteps.includes(ExecutionSteps.transcribe)) {
                    setSelectedSteps(selectedSteps.filter(s => s !== ExecutionSteps.transcribe))
                  }
                }} />} label="Transcribe"></FormControlLabel>
              </FormGroup>
              <FormGroup>
                <FormControlLabel control={<Checkbox checked={selectedSteps.includes(ExecutionSteps.speakerTagging)} value={ExecutionSteps.speakerTagging} onChange={e => {
                  if (e.target.checked && !selectedSteps.includes(ExecutionSteps.speakerTagging)) {
                    setSelectedSteps([...selectedSteps, ExecutionSteps.speakerTagging])
                    selectedSteps.push(ExecutionSteps.speakerTagging)
                  }
                  if (!e.target.checked && selectedSteps.includes(ExecutionSteps.speakerTagging)) {
                    setSelectedSteps(selectedSteps.filter(s => s !== ExecutionSteps.speakerTagging))
                  }
                }} />} label="Speaker Tagging"></FormControlLabel>
              </FormGroup>
            </Row>
            <hr className="my-2" />
            <Row className="ml-1">
              {/* <Col className="my-2" md="3" xs="6"> */}
              <Button className="p-0">
                <label className="pointer mb-0 py-2 px-4">
                  <input
                    onChange={e => {
                      if (e.target.files) {
                        setFile(e.target.files)
                      }
                    }}
                    style={{ display: 'none' }}
                    type="file"
                    accept="video/mp4,video/x-matroska,.mkv,.mp4,audio/mp3,audio/wav,audio/m4a,.m4a,.mp3,.wav"
                  />
                  {isLoading ? `${progress}% Uploaded` : 'Upload Video/Audio'}
                </label>
              </Button>
              <UncontrolledDropdown>
                <DropdownToggle caret>
                  {!type ? 'Conference Type' : (confTypeToString(type))}
                </DropdownToggle>
                <DropdownMenu>
                  {
                    Object.entries(ConfType).map((t, i) => (
                      <DropdownItem key={i} onClick={() => {
                        setType(t[1])
                      }}>
                        {confTypeToString(t[1])}
                      </DropdownItem>
                    ))
                  }
                </DropdownMenu>
              </UncontrolledDropdown>
              <Button
                onClick={handleAnalyse}
              >
                Analyse Video/Audio
              </Button>
              {/* </Col> */}
              {/* <Col className="my-2" md="3" xs="6">
                <Button
                  onClick={handleAnalyse}
                >
                  Search Now
                </Button>
              </Col> */}
              {/* <Col className="my-2" md="6" xs="12">
                <Button color="primary" className="ml-4" onClick={() => {
                  alert('Work in progress!')
                }}>
                  Schedule For Later
                </Button>
                <TextField
                  id="datetime-local"
                  label="Schedule For Later"
                  type="datetime-local"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Col> */}
            </Row>
          </div>
        </Container>
      </div>
    </>
  );
};

export default Scheduler;
